import angular from 'angular';
import Raven from 'raven-js';
import _ from 'lodash';
import Logger from './shared/logger';
import UtilsService from './services/utilsService';

const pageTitlesWithPublicAccess = [
    'Login',
    'Employer request demo',
    'Employer sing up',
    'Recruiter sing up',
    'Candidate Approval',
    'Employer Rep sign up',
    'Public Candidate Page',
    'Insight Report With Funnel',
    'Employer job resumes for employer rep',
];

const checkIfPublicPageByTitle = (pageTitle) => pageTitlesWithPublicAccess.some((publicPageTitle) => publicPageTitle === pageTitle);

export function config(
    $stateProvider,
    $ocLazyLoadProvider,
    $urlRouterProvider,
    angularAuth0Provider,
    $locationProvider,
    $cookiesProvider,
    RestangularProvider,
    EmployerRepServiceProvider,
    EnvSettings,
    AUTH0_CONSTS,
    $translateProvider,
    $localStorageProvider,
    EMPLOYER_REP_AUTH_FLOW,
    EMPLOYER_WORKFLOW_STATUS,
    WORKFLOW_STEP_UNKNOWN,
    AuthServiceProvider,
) {
    $urlRouterProvider.otherwise(async ($injector) => {
        const $state = $injector.get('$state');
        const $localStorage = $injector.get('$localStorage');
        const $cookies = $injector.get('$cookies');
        const permissions = $injector.get('permissions');
        const PositionService = $injector.get('PositionService');
        const WebAppRouteService = $injector.get('WebAppRouteService');
        const EmployerRepService = $injector.get('EmployerRepService');
        UtilsService.cookiesOverride($cookies);
        const isTalenyaUser = permissions.isTalenyaUser();
        const isTalentExpertUser = permissions.isTalentExpertUser();
        const isEmployerUser = permissions.isEmployerUser();
        const employerRepActionType = _.get($localStorage, 'employerRepReturnUrlParams.actionType', null);
        const isUserEmployerRepToFeedBack = employerRepActionType === EMPLOYER_REP_AUTH_FLOW.FEEDBACK;
        const isUserEmployerRepToDiversity = employerRepActionType === EMPLOYER_REP_AUTH_FLOW.DIVERSITY;
        const isUserEmployerRepToSendToEmployer = employerRepActionType === EMPLOYER_REP_AUTH_FLOW.SUBMIT_TO_EMPLOYER;
        const isUserEmployerRepToCompleteJobDefinition = employerRepActionType === EMPLOYER_REP_AUTH_FLOW.COMPLETE_JOB_DEFINITION_UPLOAD_JOB_WIZARD;

        const isUserAuthenticated = $cookies.get('authResult');
        const userData = UtilsService.parseJSON($cookies.get('userData'));
        const isUserAuthorized = isUserAuthenticated && userData?.roles;

        if (isUserAuthorized) {
            if (isUserEmployerRepToFeedBack) {
                const opts = {
                    positionId: $localStorage.employerRepReturnUrlParams.positionId,
                    employerId: $localStorage.employerRepReturnUrlParams.employerId,
                    workflowStep: EMPLOYER_WORKFLOW_STATUS.FEEDBACK,
                    scroll: 0,
                    currCandidatePage: 1,
                    talentID: null,
                    feedbackFilterMode: null,
                };
                $localStorage.employerRepReturnUrlParams = null;

                if (!opts.employerId || !opts.positionId) {
                    throw new Error('employerId or (and) positionId is not provided for redirection to feedback wizard page');
                }

                return WebAppRouteService.goToEmployerCandidatesPage(opts);
            }

            if (isUserEmployerRepToDiversity) {
                const opts = {
                    positionId: $localStorage.employerRepReturnUrlParams.positionId,
                    employerId: $localStorage.employerRepReturnUrlParams.employerId,
                };
                $localStorage.employerRepReturnUrlParams = null;

                if (!opts.employerId || !opts.positionId) {
                    throw new Error('employerId or (and) positionId is not provided for redirection to diversity optimizer page');
                }

                return WebAppRouteService.goToDiversityOptimizerPage(opts);
            }

            if (isUserEmployerRepToSendToEmployer) {
                const isBulkOperation = !$localStorage.employerRepReturnUrlParams.talentId;
                const isShouldOpenCandidateListPage = isBulkOperation;
                const opts = {
                    positionId: $localStorage.employerRepReturnUrlParams.positionId,
                    employerId: $localStorage.employerRepReturnUrlParams.employerId,
                    workflowStep: isShouldOpenCandidateListPage ? EMPLOYER_WORKFLOW_STATUS.REVIEWED : WORKFLOW_STEP_UNKNOWN,
                    scroll: 0,
                    currCandidatePage: 1,
                    talentID: $localStorage.employerRepReturnUrlParams.talentId,
                    feedbackFilterMode: null,
                };
                $localStorage.employerRepReturnUrlParams = null;

                if (opts.talentID) {
                    return WebAppRouteService.goToEmployerCandidatePage(opts);
                }

                return WebAppRouteService.goToEmployerCandidatesPage(opts);
            }

            if (isUserEmployerRepToCompleteJobDefinition) {
                const opts = {
                    positionId: $localStorage.employerRepReturnUrlParams.positionId,
                    employerId: $localStorage.employerRepReturnUrlParams.employerId,
                };

                $localStorage.employerRepReturnUrlParams = null;

                if (!opts.employerId || !opts.positionId) {
                    throw new Error('employerId or (and) positionId is not provided for redirection to upload wizard page');
                }
                const { stepName } = await PositionService.getUploadJobCurrentWizardStep(opts.positionId);

                return WebAppRouteService.goToViewUploadWizardPage({
                    ...opts,
                    stepName,
                });
            }

            const userPrefs = UtilsService.parseJSON($cookies.get('userprefs'));
            const origUserPrefs = UtilsService.parseJSON($cookies.get('origUserPrefs'));

            if (isTalenyaUser) {
                if (isTalentExpertUser && userPrefs?.lastSelectedEmployerId) {
                    WebAppRouteService.goPositionsPage({
                        employerId: userPrefs.lastSelectedEmployerId,
                    });
                } else if (isTalentExpertUser && userPrefs?.entityId) {
                    $state.go('talenya.jobs', { recruiterId: userPrefs.entityId });
                } else {
                    $state.go('talenya.jobs');
                }
            } else if (userData.roles.indexOf('AM') >= 0) {
                if (isTalentExpertUser && userPrefs?.entityId) {
                    $state.go('recruiter.positions', { recruiterId: userPrefs.entityId });
                } else {
                    $state.go('accountManager.dashboard');
                }
            } else if (isTalentExpertUser) {
                if (userPrefs?.entityId) {
                    $state.go('talenya.jobs', { recruiterId: userPrefs.entityId });
                } else {
                    $state.go('recruiter.positions', { recruiterId: '000000000000000000000000' });
                }
            } else if (isEmployerUser) {
                const { result: employerRep } = await EmployerRepService.getExistingEmployerRep(origUserPrefs?.employerRepId);
                if (!employerRep) {
                    throw new Error('employerRep does not exist');
                }
                if (employerRep.employerId) {
                    WebAppRouteService.goPositionsPage({
                        employerId: employerRep.employerId,
                    });
                }
            }
            return;
        }
        $state.go('logout');
    });

    $ocLazyLoadProvider.config({
        debug: false,
    });

    $stateProvider
        .state('login', {
            url: '/login/:isEmployerRep',
            params: {
                employerRepId: null,
            },
            template: '<login-component></login-component>',
            data: { pageTitle: 'Login' },
            resolve: {
                loadPlugin($ocLazyLoad) {
                    return $ocLazyLoad.load([]);
                },
            },
        })
        .state('logout', {
            url: '/logout',
            data: { pageTitle: 'logout' },
            controller: ['$state', async ($state) => {
                await AuthServiceProvider.$get().logout(true);
                $state.go('login');
            }],
        })
        .state('search', {
            url: '/search/searchHistory/:currSearchHistoryIndex/candidatesPage/:currCandidatePage/scroll/:scroll/candidate/:talentID/searchName/:searchName/candidatesName/:candidatesName',
            template: '<search-history></search-history>',
            data: { pageTitle: 'Search' },
            resolve: {
                loadPlugin($ocLazyLoad) {
                    return $ocLazyLoad.load([]);
                },
            },
            params: {
                currSearchHistoryIndex: null,
                currCandidatePage: null,
                talentID: null,
                scroll: null,
                searchName: null,
                candidatesName: null,
            },
        })
        .state('searchNew', {
            url: '/search',
            template: '<search-history-new></search-history-new>',
            data: { pageTitle: 'Search' },
            resolve: {
                loadPlugin($ocLazyLoad) {
                    return $ocLazyLoad.load([]);
                },
            },
        })
        .state('employer', {
            url: '/employer/:employerId',
            abstract: true,
            template: '<div class="recruiter recruiter-jobs-table"><employer-component></employer-component></div>',
            data: { pageTitle: 'Employer' },
            resolve: {
                loadPlugin($ocLazyLoad) {
                    return $ocLazyLoad.load([]);
                },
            },
            params: { employerId: null },
        })
        .state('employer.createJob', {
            url: '/createJob/:positionId',
            template: '<create-job-component></create-job-component>',
            data: { pageTitle: 'Employer Create Job' },
            params: { positionId: null },
        })
        .state('employer.jobs', {
            url: '/jobs',
            template: '<employer-jobs-component></employer-jobs-component>',
            data: { pageTitle: 'Employer Jobs' },
            resolve: {
                loadPlugin($ocLazyLoad) {
                    return $ocLazyLoad.load([]);
                },
            },
        })
        .state('employer.jobResumesForEmployerRep', {
            url: '/jobResumes/:positionId/employerRep/:employerRepId/:actionType?talentId',
            data: { pageTitle: 'Employer job resumes for employer rep' },
            resolve: {
                logout: ['AuthService', 'EmployerRepService', '$stateParams', '$localStorage', '$q',
                    'EnvSettings', '$state', '$timeout',
                    async (
                        AuthService,
                        EmployerRepService,
                        $stateParams,
                        $localStorage,
                        $cookies,
                        $q,
                        EnvSettings,
                        $state,
                        $timeout,
                    ) => {
                        UtilsService.cookiesOverride($cookies);
                        const userPrefs = UtilsService.parseJSON($cookies.get('userprefs'));
                        const isAuthenticated = !!AuthService.isAuthenticated();
                        const currentEmployerRepId = isAuthenticated ? userPrefs.entityId : null;
                        const targetEmployerRepId = $stateParams.employerRepId;
                        const targetEmployerId = $stateParams.employerId;
                        const targetPositionId = $stateParams.positionId;
                        const targetTalentId = $stateParams.talentId;

                        $localStorage.employerRepReturnUrlParams = {
                            positionId: targetPositionId,
                            employerId: targetEmployerId,
                            targetEmployerRepId,
                            actionType: $stateParams.actionType,
                            talentId: targetTalentId,
                        };

                        try {
                            const { result: { action } = {} } = await EmployerRepService.getAuthenticationActionForEmployerFeedback({
                                currentUserId: currentEmployerRepId,
                                host: `${EnvSettings.Auth0RedirectUrl}`,
                                targetEmployerRepId,
                            }) || {};

                            if (action === 'LOGIN') {
                                await AuthService.logout(true);
                                $timeout(() => $state.go('login', { isEmployerRep: 1 }));
                            } else if (action === 'SIGNUP') {
                                $timeout(() => $state.go('employerRep.signUp.details', {
                                    employerRepId: targetEmployerRepId,
                                }));
                            }
                        } catch (err) {
                            Logger.error(err);
                            await AuthService.logout(true);
                            $timeout(() => $state.go('login', { isEmployerRep: 1 }));
                        }

                        return $q.reject();
                    }],
            },
            params: {
                positionId: null,
                employerRepId: null,
                actionType: null,
                talentId: null,
            },
        })
        .state('employerRep', {
            url: '/employerRep/:employerRepId',
            abstract: true,
            template: '<div ui-view></div>',
            params: { employerRepId: null },
        })
        .state('employerRep.signUp', {
            url: '/sign-up',
            abstract: true,
            template: '<div ui-view></div>',
        })
        .state('employerRep.signUp.details', {
            url: '/details/:isInvitation',
            template: '<employer-rep-sign-up-details></employer-rep-sign-up-details>',
            data: { pageTitle: 'Employer Rep sign up' },
            params: {
                employerRepId: null,
                isCreateNewEmplRepAction: false,
                isInvitation: false,
            },
        })
        .state('employerRep.signUp.detailsNew', {
            url: '/details/:isInvitation/:invitationId',
            template: '<employer-rep-sign-up-details></employer-rep-sign-up-details>',
            data: { pageTitle: 'Employer Rep sign up' },
            params: {
                employerRepId: null,
                isCreateNewEmplRepAction: false,
                isInvitation: false,
                invitationId: '',
            },
        })
        .state('employerRep.signUp.email', {
            url: '/email',
            template: '<employer-rep-sign-up-email></employer-rep-sign-up-email>',
            data: { pageTitle: 'Employer Rep sign up' },
            params: {
                employerRepId: null,
                employerId: null,
                isSso: null,
            },
        })
        .state('employerRep.signUp.password', {
            url: '/password',
            template: '<employer-rep-sign-up-password></employer-rep-sign-up-password>',
            data: { pageTitle: 'Employer Rep sign up' },
            params: {
                employerRepId: null,
                employerId: null,
                isSso: null,
                successUrl: null,
            },
        })
        .state('employerRep.signUp.passwordNew', {
            url: '/password/:invitationId',
            template: '<employer-rep-sign-up-password></employer-rep-sign-up-password>',
            data: { pageTitle: 'Employer Rep sign up' },
            params: {
                employerRepId: null,
                employerId: null,
                isSso: null,
                successUrl: null,
                invitationId: null,
            },
        })
        .state('employerRep.signUp.activate', {
            url: '/activate',
            template: '<employer-rep-activate></employer-rep-activate>',
            data: { pageTitle: 'Employer sign up' },
            params: {
                employerRepId: null,
            },
        })
        .state('employerRep.signUp.activateLinkedin', {
            url: '/activate-linkedin',
            template: '<employer-rep-linkedin-activate-component></employer-rep-linkedin-activate-component>',
            data: { pageTitle: 'Employer sign up' },
            params: {
                employerRepId: null,
            },
        })
        .state('employerRep.signUp.emailVerification', {
            url: '/:positionId/:employerId/:actionType/email-verification',
            data: { pageTitle: 'Employer Rep sign up' },
            resolve: {
                logout: ['EmployerRepService', '$stateParams', '$q', '$state', '$timeout', '$localStorage',
                    async (EmployerRepService, $stateParams, $q, $state, $timeout, $localStorage) => {
                        const {
                            employerRepId, actionType, positionId, employerId,
                        } = $stateParams;

                        if (!employerRepId) {
                            $q.reject();
                        }
                        try {
                            $localStorage.employerRepReturnUrlParams = { ...$localStorage.employerRepReturnUrlParams };
                            $localStorage.employerRepReturnUrlParams.positionId = positionId;
                            $localStorage.employerRepReturnUrlParams.employerId = employerId;
                            $localStorage.employerRepReturnUrlParams.actionType = actionType;
                            $localStorage.employerRepReturnUrlParams.targetEmployerRepId = employerRepId;

                            await EmployerRepService.confirmEmail({ employerRepId });

                            $timeout(() => $state.go('employerRep.signUp.password', { employerRepId }));

                            return $q.reject();
                        } catch (err) {
                            Logger.error(err);
                            return $q.reject();
                        }
                    }],
            },
            params: {
                employerRepId: null,
                actionType: null,
                positionId: null,
            },
        })
        .state('employer.job', {
            url: '/job/:positionId',
            template: '<employer-job-component></employer-job-component>',
            resolve: {
                loadPlugin($ocLazyLoad) {
                    return $ocLazyLoad.load([]);
                },
            },
            params: {
                positionId: null,
            },
            data: { pageTitle: 'Employer Job' },
            onEnter($stateParams, WebAppRouteService, $window) {
                $window.document.title = 'Employer Job'
                return WebAppRouteService.goToViewJobPage($stateParams);
            },
        })
        .state('employer.editEmployer', {
            url: '/editEmployer/:empId',
            template: '<create-new-employer></create-new-employer>',
            data: { pageTitle: 'Edit Employer' },
            resolve: {
                loadPlugin($ocLazyLoad) {
                    return $ocLazyLoad.load([]);
                },
            },
            params: {
                empId: null,
            },
        })
        .state('employer.defaultAssociatedEmployersSettings', {
            url: '/editEmployer/:empId/defaultAssociatedEmployersSettings',
            template: '<default-associated-employers-settings></default-associated-employers-settings>',
            data: { pageTitle: 'Edit Default Associated Employers Settings' },
            resolve: {
                loadPlugin($ocLazyLoad) {
                    return $ocLazyLoad.load([]);
                },
            },
            params: {
                empId: null,
                resellerEmployerData: {},
            },
        })
        .state('employer.createAssociatedEmployer', {
            url: '/createAssociatedEmployer',
            template: '<create-new-employer></create-new-employer>',
            data: { pageTitle: 'Create Associate Employer' },
            resolve: {
                loadPlugin($ocLazyLoad) {
                    return $ocLazyLoad.load([]);
                },
            },
            params: {
                isResellerAssociatedEmployer: true,
                empId: null,
            },
        })
        .state('employer.manageUsers', {
            url: '/manageUsers',
            template: '<employer-manage-users-component></employer-manage-users-component>',
            data: { pageTitle: 'Employer Manage Users' },
            resolve: {
                loadPlugin($ocLazyLoad) {
                    return $ocLazyLoad.load([]);
                },
            },
        })
        .state('employer.search', {
            url: '/search/position/:positionId/searchHistory/:currSearchHistoryIndex/candidatesPage/:currCandidatePage/scroll/:scroll/candidate/:talentID/searchName/:searchName/candidatesName/:candidatesName',
            template: '<employer-search></employer-search>',
            data: { pageTitle: 'Employer Search Page' },
            resolve: {
                loadPlugin($ocLazyLoad) {
                    return $ocLazyLoad.load([]);
                },
            },
            params: {
                employerId: null,
                positionId: null,
                currSearchHistoryIndex: null,
                currCandidatePage: null,
                talentID: null,
                scroll: null,
                searchName: null,
                candidatesName: null,
            },
        })
        .state('employer.searchNew', {
            url: '/search/position/:positionId',
            template: '<employer-search-new></employer-search-new>',
            data: { pageTitle: 'Employer Search Page' },
            resolve: {
                loadPlugin($ocLazyLoad) {
                    return $ocLazyLoad.load([]);
                },
            },
            params: {
                history: { dynamic: true },
                page: { dynamic: true },
                scroll: { dynamic: true },
                talentID: { dynamic: true },
                searchName: { dynamic: true },
                candidatesName: { dynamic: true },
            },
        })
        .state('recruiter', {
            url: '/recruiter/:recruiterId',
            abstract: true,
            template: '<div class="recruiter recruiter-jobs-table"><recruiter-component></recruiter-component></div>',
            data: { pageTitle: 'Recruiter' },
            resolve: {
                loadPlugin($ocLazyLoad) {
                    return $ocLazyLoad.load([]);
                },
            },
            params: { recruiterId: null },
        })
        .state('recruiter.positions', {
            url: '/positions',
            template: '<recruiter-positions-component></recruiter-positions-component>',
            data: { pageTitle: 'Recruiter Positions' },
            resolve: {
                loadPlugin($ocLazyLoad) {
                    return $ocLazyLoad.load([]);
                },
            },
        })
        .state('recruiter.openPosition', {
            url: '/openPosition/:positionId',
            template: '<recruiter-open-job-component></recruiter-open-job-component>',
            data: { pageTitle: 'Recruiter Open Position' },
            resolve: {
                loadPlugin($ocLazyLoad) {
                    return $ocLazyLoad.load([]);
                },
            },
            params: {
                positionId: null,
            },
        })
        .state('recruiter.candidatesJob', {
            url: '/candidatesJob/:positionId/workflowStep/:workflowStep/candidatesPage/:currCandidatePage/scroll/:scroll/candidate/:talentID/:showHidden/:claimedByOtherTE/:canSendToEmployer?feedbackFilterMode',
            template: '<recruiter-candidates-job-component></recruiter-candidates-job-component>',
            data: { pageTitle: 'Recruiter Candidates Job' },
            resolve: {
                loadPlugin($ocLazyLoad) {
                    return $ocLazyLoad.load([]);
                },
            },
            params: {
                positionId: null,
                currCandidatePage: null,
                scroll: null,
                talentID: null,
                workflowStep: null,
                showAll: null,
                claimedByOtherTE: null,
                canSendToEmployer: 'false',
                isFeedbackToSamplesRedirectIfEmpty: true,
                feedbackFilterMode: null,
            },
        })
        .state('recruiter.editJob', {
            url: '/editJob/:positionId/:employerId',
            template: '<create-job-component></create-job-component>',
            data: { pageTitle: 'Recruiter Edit Job' },
            resolve: {
                loadPlugin($ocLazyLoad) {
                    return $ocLazyLoad.load([]);
                },
            },
            params: { positionId: null, employerId: null },
        })
        .state('recruiter.search', {
            url: '/search/position/:positionId/searchHistory/:currSearchHistoryIndex/candidatesPage/:currCandidatePage/scroll/:scroll/candidate/:talentID/searchName/:searchName/candidatesName/:candidatesName/:claimedByOtherTE',
            template: '<recruiter-search></recruiter-search>',
            data: { pageTitle: 'Recruiter Search Page' },
            resolve: {
                loadPlugin($ocLazyLoad) {
                    return $ocLazyLoad.load([]);
                },
            },
            params: {
                recruiterId: null,
                positionId: null,
                currSearchHistoryIndex: null,
                currCandidatePage: null,
                talentID: null,
                scroll: null,
                searchName: null,
                candidatesName: null,
                claimedByOtherTE: null,
            },
        })
        .state('recruiter.searchNew', {
            url: '/search/position/:positionId',
            template: '<recruiter-search-new></recruiter-search-new>',
            data: { pageTitle: 'Recruiter Search Page' },
            resolve: {
                loadPlugin($ocLazyLoad) {
                    return $ocLazyLoad.load([]);
                },
            },
            params: {
                history: { dynamic: true },
                page: { dynamic: true },
                scroll: { dynamic: true },
                talentID: { dynamic: true },
                searchName: { dynamic: true },
                candidatesName: { dynamic: true },
                claimedByOtherTE: { dynamic: true },
            },
        })
        .state('recruiter.createNewCandidate', {
            url: '/createNewCandidate',
            template: '<create-new-candidate></create-new-candidate>',
            data: { pageTitle: 'Create New Candidate' },
            resolve: {
                loadPlugin($ocLazyLoad) {
                    return $ocLazyLoad.load([]);
                },
            },
        })
        .state('talenya', {
            url: '/talenya',
            abstract: true,
            template: '<div class="recruiter recruiter-jobs-table"><talenya-component></talenya-component></div>',
            data: { pageTitle: 'Talenya' },
            resolve: {
                loadPlugin($ocLazyLoad) {
                    return $ocLazyLoad.load([]);
                },
            },
            params: {},
        })
        .state('talenya.dashboard', {
            url: '/dashboard/:type',
            template: '<dashboard-graphs-component></dashboard-graphs-component>',
            data: { pageTitle: 'Talenya dashboardGraphs' },
            resolve: {
                loadPlugin($ocLazyLoad) {
                    return $ocLazyLoad.load([]);
                },
            },
            params: {
                type: null,
            },
        })
        .state('talenya.jobs', {
            url: '/jobs',
            template: '<talenya-job-component></talenya-job-component>',
            data: { pageTitle: 'Talenya Jobs' },
            resolve: {
                loadPlugin($ocLazyLoad) {
                    return $ocLazyLoad.load([]);
                },
            },
            params: {},
        })
        .state('talenya.createNewEmployer', {
            url: '/createNewEmployer',
            template: '<create-new-employer></create-new-employer>',
            data: { pageTitle: 'Create New Employer' },
            resolve: {
                loadPlugin($ocLazyLoad) {
                    return $ocLazyLoad.load([]);
                },
            },
        })
        .state('talenya.createNewRecruiter', {
            url: '/createRecruiter/',
            template: '<create-edit-recruiter></create-edit-recruiter>',
            data: { pageTitle: 'Create New Recruiter' },
            resolve: {
                loadPlugin($ocLazyLoad) {
                    return $ocLazyLoad.load([]);
                },
            },
            params: {},
        })
        .state('recruiter.editRecruiter', {
            url: '/editRecruiter/:recId',
            template: '<create-edit-recruiter></create-edit-recruiter>',
            data: { pageTitle: 'Edit Recruiter' },
            resolve: {
                loadPlugin($ocLazyLoad) {
                    return $ocLazyLoad.load([]);
                },
            },
            params: {
                recId: null,
            },
        })
        .state('accountManager', {
            url: '/accountManager',
            abstract: true,
            template: '<div class="recruiter recruiter-jobs-table"><account-manager-component></account-manager-component></div>',
            data: { pageTitle: 'Talenya' },
            resolve: {
                loadPlugin($ocLazyLoad) {
                    return $ocLazyLoad.load([]);
                },
            },
            params: {},
        })
        .state('accountManager.dashboard', {
            url: '/dashboard',
            template: '',
            data: { pageTitle: 'Talenya dashboardGraphs' },
            resolve: {
                loadPlugin($ocLazyLoad) {
                    return $ocLazyLoad.load([]);
                },
            },
            params: {},
        })
        .state('register', {
            url: '/register',
            template: '<register></register>',
            data: { pageTitle: 'Register' },
        })
        .state('addEmployer', {
            url: '/addEmployer',
            template: '<create-new-employer></create-new-employer>',
            data: { pageTitle: 'Create New Employer' },
        })
        .state('talentExpertSignup', {
            url: '/talentExpertSignup',
            template: '<recruiter-singup-component></recruiter-singup-component>',
            data: { pageTitle: 'Recruiter sign up' },
        })
        .state('addTalentExpert', {
            url: '/addTalentExpert',
            template: '<create-edit-recruiter></create-edit-recruiter>',
            data: { pageTitle: 'Create New Recruiter' },
            resolve: {
                loadPlugin($ocLazyLoad) {
                    return $ocLazyLoad.load([]);
                },
            },
        })
        .state('candidateApproval', {
            url: '/candidateApproval/:talentId/:workflowId',
            template: '<candidate-approval-component></candidate-approval-component>',
            data: { pageTitle: 'Candidate Approval' },
            resolve: {
                loadPlugin($ocLazyLoad) {
                    return $ocLazyLoad.load([]);
                },
            },
            params: {
                talentId: null,
                workflowId: null,
            },
        })
        .state('publicProfile', {
            url: '/public/profile/:talentId/:workflowId/:workflowDate/:profileForSubmissionParam',
            template: '<public-candidate-profile></public-candidate-profile>',
            data: { pageTitle: 'Public Candidate Page' },
            resolve: {
                loadPlugin($ocLazyLoad) {
                    return $ocLazyLoad.load([]);
                },
            },
            params: {
                candidateId: null,
                workflowId: null,
                workflowDate: null,
                profileForSubmissionParam: null,
            },
        })
        .state('insightReport', {
            url: '/insightReport/:positionId',
            template: '<report-funnel-component></report-funnel-component>',
            data: { pageTitle: 'Insight Report With Funnel' },
            resolve: {
                loadPlugin($ocLazyLoad) {
                    return $ocLazyLoad.load([]);
                },
            },
            params: {
                positionId: null,
            },
        });

    RestangularProvider.setBaseUrl(EnvSettings.BackendUrl);
    RestangularProvider.setDefaultHttpFields({
        withCredentials: true,
    });
    RestangularProvider.setErrorInterceptor((response) => (response.status !== 403));

    let $cookies;
    angular.injector(['ngCookies']).invoke(['$cookies', (_$cookies_) => {
        $cookies = _$cookies_;
    }]);
    UtilsService.cookiesOverride($cookies);

    const isUserAuthenticated = $cookies.get('authResult');
    if (isUserAuthenticated) {
        const userData = UtilsService.parseJSON($cookies.get('userData'));

        if (userData) {
            Raven.setUserContext({
                id: userData.user_id,
                email: userData.email,
            });
        } else {
            Logger.warn(new Error('User data is not defined'));
        }
    }

    // Initialization for the angular-auth0 library
    angularAuth0Provider.init({
        clientID: AUTH0_CONSTS.clientId,
        domain: AUTH0_CONSTS.domain,
        responseType: 'token id_token',
        redirectUri: EnvSettings.Auth0RedirectUrl,
        leeway: 60,
    });

    // Remove the ! from the hash so that auth0.js can properly parse it
    $locationProvider.hashPrefix('');

    // TFT-4882 clear all auth info to avoid circular redirects
    if (window.location && window.location.hash.includes('dropAuth')) {
        AuthServiceProvider.$get().logout(true);
    }

    $translateProvider.preferredLanguage('en-US');
    $translateProvider.useInterpolation('customInterpolation');
    $translateProvider.useLoader('customLoader');
}

angular
    .module('TALENYA-FE')
    .factory('AuthInterceptor', ($rootScope, $q, AUTH_EVENTS, ERROR_EVENTS) => ({
        responseError(response) {
            if (response.config.url.indexOf('uiLanguages') >= 0 || response.config.url.indexOf('logout') >= 0) {
                return;
            }

            if (response.status === -1 && !response.config.disableNotifications) {
                response.status = 999;
            }

            if (response.status === 500 || response.status === 999) {
                Logger.error('AuthInterceptor track status =>', JSON.stringify(response));
            }

            $rootScope.$broadcast({
                401: AUTH_EVENTS.notAuthenticated,
                403: AUTH_EVENTS.notAuthorized,
                404: ERROR_EVENTS.notFound,
                419: AUTH_EVENTS.sessionTimeout,
                440: AUTH_EVENTS.sessionTimeout,
                500: ERROR_EVENTS.serverError,
                999: ERROR_EVENTS.frontTimeout,
            }[response.status], response);

            return $q.reject(response);
        },
    }))
    .config(($httpProvider) => {
        $httpProvider.interceptors.push(['$injector', ($injector) => $injector.get('AuthInterceptor')]);
    })
    .config(config)
    .run((
        $cookies,
        $rootScope,
        $state,
        $location,
        AUTH_EVENTS,
        EnvSettings,
        AuthService,
        $localStorage,
        FE_VERSION,
        NotificationService,
        ERROR_EVENTS,
        Restangular,
    ) => {
        // Put the authService on $rootScope so its methods can be accessed from the nav bar
        $rootScope.auth = AuthService;

        // Process the auth token if it exists and fetch the profile
        AuthService.handleParseHash();

        $rootScope.$state = $state;

        UtilsService.cookiesOverride($cookies);

        Restangular.addFullRequestInterceptor((element, operation, route, url, headers, params, httpConfig) => {
            const pageTitle = _.get($state, 'current.data.pageTitle');
            const isPublicPage = checkIfPublicPageByTitle(pageTitle);

            if (isPublicPage) {
                return;
            }

            const authResult = $cookies.get('authResult');

            if (authResult) {
                return {
                    element,
                    params,
                    headers: {
                        ...headers,
                    },
                    httpConfig,
                };
            }

            if (!_.endsWith(url, '/logout') && !_.endsWith(url, '/login') && !_.endsWith(url, 'auth/data')) {
                Logger.error(`RestangularProvider track status => ${JSON.stringify({
                    element, operation, route, url, headers, params, httpConfig,
                })}`);

                $rootScope.$broadcast(AUTH_EVENTS.notAuthenticated);
            }
        });

        $rootScope.$on(AUTH_EVENTS.notAuthenticated, async () => {
            UtilsService.cookiesOverride($cookies);
            const authResult = $cookies.get('authResult');
            if (!authResult) {
                await AuthService.logout();
            }
        });

        $rootScope.$on(AUTH_EVENTS.notAuthorized, async () => {
            NotificationService.error('AUTHORIZATION_ERROR');
            await AuthService.logout();
        });

        $rootScope.$on(ERROR_EVENTS.serverError, (event, response) => {
            Logger.warn(event, response);

            if (response && response.data) {
                NotificationService.error(response.data.message);
            }
        });

        $rootScope.$on(ERROR_EVENTS.frontTimeout, () => {
            NotificationService.error('Session timeout');
        });

        $rootScope.$on('$stateChangeStart', (event, next, param) => {
            const { pageTitle } = next.data;
            const isPublicPage = checkIfPublicPageByTitle(pageTitle);
            const canAccessPage = isPublicPage || AuthService.isAuthenticated();

            if (!canAccessPage) {
                event.preventDefault();

                $localStorage.nextState = {
                    state: next.name,
                    params: param,
                };

                $rootScope.$broadcast(AUTH_EVENTS.notAuthenticated);
            }
        });
    });
